import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchAffiliateLinks, optInToAffiliate } from "./affiliateLinks";
import { Spinner } from "./PageLoader";

/**
 * "Earn by sharing this" — the affiliate section of a member's profile.
 *
 * A creator can open some of their products up to the people who bought a
 * particular one. This shows those people what they may share, and gives them
 * a way to sign up for it.
 *
 * ── Renders nothing unless it has something to say ──────────────────────────
 * Most members, in most memberships, are not affiliates and never will be. So
 * the default is invisible: no heading, no empty state, no "you have no
 * links". It appears only when the creator has opened something up AND this
 * member bought the product that opens it.
 *
 * ── Nothing to sign up for ──────────────────────────────────────────────────
 * One press and they have links. No password, no account to manage, no second
 * platform to learn: somebody pressing a button in a course player does not
 * want to be handed a login somewhere else, and does not need one. Whatever
 * has to exist to track their commission exists on the server and is never
 * named here.
 *
 * ── The links only ─────────────────────────────────────────────────────────
 * The figures used to sit at the bottom of this panel, because there was
 * nowhere else to put them. There is now: AffiliateEarningsPage. A member who
 * sells eighty times across thirty products is reading a table, and a table
 * does not fit in a popup — while the links themselves are a one-off, copied
 * once and never looked at again.
 *
 * So the two swapped places. This stays the quick thing: here is what you can
 * share, here is the button. `onSeeEarnings` is the way through to the money.
 */
export default function AffiliateEarnSection({
  token,
  memberToken,
  primary = "#0274F7",
  // Inside a modal that already draws the heading and the card. Without this
  // the section would repeat both.
  bare = false,
  onChanged,
  // How to open the earnings page. Passed in rather than navigated to from
  // here: this section also renders inside the profile, which is not always
  // under a router, and a component that only works in some of its homes is
  // a component that breaks in the others.
  onSeeEarnings,
}) {
  const [state, setState] = useState(null);
  // Separate from `state`, because null means two different things — still
  // asking, and asked and got nothing — and the modal has to tell them apart.
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    let active = true;

    if (!token || !memberToken) {
      setLoading(false);

      return undefined;
    }

    (async () => {
      try {
        const data = await fetchAffiliateLinks({ token, memberToken });
        if (active) {
          setState(data);
          setFailed(false);
        }
      } catch {
        // Silent on purpose. This is an extra; a member who has never seen it
        // does not miss it, and an error banner here would be noise on a
        // profile page that is otherwise fine.
        if (active) {
          setState(null);
          setFailed(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [token, memberToken]);

  const reload = async () => {
    try {
      setState(await fetchAffiliateLinks({ token, memberToken }));
      setFailed(false);
    } catch {
      // The last good state stays on screen: this runs right after a
      // successful signup, and blanking their new links because the refresh
      // blipped would read as the signup having failed.
    }
  };

  const startSharing = async () => {
    setSubmitting(true);
    try {
      const res = await optInToAffiliate({ token, memberToken });

      if (res?.status === true) {
        toast.success(res.message || "You're all set.");
        await reload();
        // Lets the account menu re-ask: a member who has just signed up
        // should stop being offered the chance to.
        onChanged?.();
      } else {
        toast.error(res?.message || "Could not set that up just now.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not set that up just now.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (link, productId) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(productId);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error("Could not copy. Select the link and copy it manually.");
    }
  };

  const nothingToShow =
    !state || (!state.can_opt_in && state.links.length === 0);

  // In the profile this is an uninvited extra, so it stays silent: a skeleton
  // that appears and vanishes on every member who is not eligible is worse
  // than nothing. In its own modal the member ASKED for it and is watching an
  // empty box, so every state has to say something.
  if (!bare && (loading || nothingToShow)) {
    return null;
  }

  if (bare && loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-[13px] text-gray-500">
        <Spinner size={16} />
        Loading your sharing links…
      </div>
    );
  }

  if (bare && failed) {
    return (
      <p className="py-6 text-[13px] leading-relaxed text-gray-600">
        We couldn&rsquo;t load this just now. Close this and try again in a
        moment.
      </p>
    );
  }

  if (bare && nothingToShow) {
    return (
      <p className="py-6 text-[13px] leading-relaxed text-gray-600">
        Nothing you have bought comes with products to share yet. When it does,
        your links will appear here.
      </p>
    );
  }

  const body = (
    <>
      {!bare && (
        <h3 className="text-[15px] font-semibold text-gray-900">
          Earn by sharing
        </h3>
      )}
      <p
        className={`text-[13px] leading-relaxed text-gray-500${bare ? "" : " mt-1"}`}
      >
        Share these with anyone. When they buy through your link, you earn a
        commission.
      </p>

      {state.links.length > 0 && (
        <ul className="mt-4 space-y-2">
          {state.links.map((item) => (
            <li
              key={item.product_id}
              className="rounded-xl border border-gray-200 p-3"
            >
              <p className="text-[13px] font-medium text-gray-900">
                {item.product_title}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <input
                  readOnly
                  value={item.link}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 rounded-lg border-gray-200 bg-gray-50 px-3 py-1.5 text-[12px] text-gray-600"
                />
                <button
                  type="button"
                  onClick={() => copy(item.link, item.product_id)}
                  style={{ color: primary }}
                  className="shrink-0 text-[13px] font-semibold hover:underline"
                >
                  {copied === item.product_id ? "Copied" : "Copy"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {state.can_opt_in && (
        <div className="mt-4">
          {state.promotable_products.length > 0 && (
            <p className="mb-2 text-[13px] text-gray-600">
              {state.links.length > 0 ? "Also available" : "Available"} to
              share:{" "}
              <span className="font-medium text-gray-900">
                {state.promotable_products
                  .map((p) => p.product_title)
                  .join(", ")}
              </span>
            </p>
          )}
          {/* One press. Nothing to fill in, nothing to remember, no second
              place to sign in to. */}
          <button
            type="button"
            onClick={startSharing}
            disabled={submitting}
            style={{ background: primary }}
            className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-[13px] font-semibold text-white transition hover:opacity-95 active:scale-[0.99] disabled:opacity-60"
          >
            {submitting && <Spinner size={14} />}
            {submitting ? "Setting up…" : "Earn by sharing this"}
          </button>
          <p className="mt-2 text-[12px] text-gray-500">
            We&rsquo;ll ask where to send your money once you&rsquo;ve earned
            your first commission.
          </p>
        </div>
      )}

      {/* The figures themselves live on their own page now: eighty sales
          across thirty products is a table, and a table does not fit in a
          popup. This is the way to it, for the member who came here looking
          for their money and found their links. */}
      {state.links.length > 0 && onSeeEarnings && (
        <button
          type="button"
          onClick={onSeeEarnings}
          style={{ color: primary }}
          className="mt-4 flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3 text-start transition hover:bg-gray-50"
        >
          <span>
            <span className="block text-[13px] font-semibold">
              See what you&rsquo;ve earned
            </span>
            <span className="block text-[12px] text-gray-500">
              Every sale, what it paid, and what&rsquo;s still coming
            </span>
          </span>
          <svg
            className="h-4 w-4 shrink-0 rtl:rotate-180"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </>
  );

  // In the profile it is one card among several; in its own modal the modal
  // already provides the frame.
  return bare ? (
    body
  ) : (
    <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
      {body}
    </div>
  );
}
